/* eslint-disable camelcase */
import { object, string, array, number } from 'yup';

import { LOCATION_TYPE } from 'enum/location';
import { GATEWAY_TYPE, MODEL_TYPE } from 'enum/gateway';
import { IP_MODES } from 'enum/ip';
import IPValidator, { normalizeIPv4Network } from 'utils/ipValidator';

export const validationSchema = object().shape({
  name: string()
    .trim()
    .max(250, 'Name cannot be more than 250 characters.')
    .min(3, 'Name cannot be less than 3 characters.')
    .required('Name is required'),
  category: string().required('Category is required'),
  description: string().optional().max(255, 'Description cannot be more than 255 characters'),
  gateway_type: string().oneOf(Object.values(GATEWAY_TYPE), 'Invalid Gateway Type'),
  gateway_internet_proto: string().when('gateway_type', {
    is: GATEWAY_TYPE.VGATEWAY,
    then: () => string().oneOf([IP_MODES.STATIC, IP_MODES.DHCP], 'Invalid Protocol'),
    otherwise: () => string().notRequired(),
  }),
  gateway_local_ip: string().when('gateway_type', {
    is: (val) => [GATEWAY_TYPE.VGATEWAY, GATEWAY_TYPE.WIREGUARD].includes(val),
    then: () => string().required('vGateway Local IP is required.'),
    otherwise: () => string().notRequired(),
  }),
  gateway_internet_ip: string().when(['gateway_type', 'gateway_internet_proto'], {
    is: (gateway_type, gateway_internet_proto) =>
      gateway_type === GATEWAY_TYPE.VGATEWAY && gateway_internet_proto === IP_MODES.STATIC,
    then: () => string().required('vGateway Internet IP is required.'),
    otherwise: () => string().notRequired(),
  }),
  peer_address: string().when('model', {
    is: MODEL_TYPE.AWS_SITE,
    then: () => string().required('AWS Tunnel Outside IP Address is required.'),
    otherwise: () => string().notRequired(),
  }),
  peer_id: string().when('ownPubKey', {
    is: true,
    then: () => string().required('Client Public Key is required.'),
    otherwise: () => string(),
  }),
  gateway_internet_gw: string().when(['gateway_type', 'gateway_internet_proto'], {
    is: (gateway_type, gateway_internet_proto) =>
      gateway_type === GATEWAY_TYPE.VGATEWAY && gateway_internet_proto === IP_MODES.STATIC,
    then: () => string().required('vGateway Default Route is required.'),
    otherwise: () => string().notRequired(),
  }),
  peer_networks: array().when('peer_network', {
    is: (peer_network) => peer_network && peer_network.length > 0,
    then: (schema) => schema.optional(),
    otherwise: (schema) =>
      schema.required('At least one Local Network is required').min(1, 'At least one Local Network is required'),
  }),
  peer_network: string()
    .nullable()
    .trim()
    .test('is-valid-ip', 'Invalid network', (value) => {
      if (!value) return true; // skip validation if empty
      return Boolean(normalizeIPv4Network(value)); // validate only if value is present
    }),
  peer_gateway: string()
    .nullable()
    .trim()
    .test('is-valid-ip', 'Invalid IP', (value) => {
      if (!value) return true; // skip validation if empty
      return IPValidator(value, 4); // validate only if value is present
    }),
  location: string().oneOf(Object.values(LOCATION_TYPE)).required('Location type is required'),
  lat: number().when('location', {
    is: LOCATION_TYPE.COORDINATES,
    then: (schema) => schema.required('Latitude is required'),
    otherwise: (schema) => schema.optional(),
  }),
  lng: number().when('location', {
    is: LOCATION_TYPE.COORDINATES,
    then: (schema) => schema.required('Longitude is required'),
    otherwise: (schema) => schema.optional(),
  }),
});
